/**
 * Rolling memory trajectory, attached to the `memory_runtime` Sentry context.
 *
 * Why the context and not breadcrumbs, which are the obvious home for a time
 * series: `sentry.ts` sets no `maxBreadcrumbs`, so Sentry's default ring of 100
 * is shared across every category, and this app emits console breadcrumbs
 * freely (hence that file's `beforeBreadcrumb` noise filter). Memory samples
 * would be evicted long before a crash. The context is rewritten whole on each
 * scope refresh, so it cannot be crowded out.
 *
 * Motivation: `memory_runtime` today carries ONE reading, taken as the app
 * dies. 33 production events proved the app ends at ~2.0-2.6 GiB
 * `phys_footprint`, 96-97% compressed, and said nothing about how it got
 * there. This is the missing shape of the climb.
 */

/** Samples retained. At TRAIL_STEP_S this is the window the next crash carries. */
export const TRAIL_MAX_SAMPLES = 120;

/** Seconds between trail samples. 120 x 30 s = a 60-minute window. */
export const TRAIL_STEP_S = 30;

const BYTES_PER_MIB = 1024 * 1024;

/**
 * Run-length encode a per-sample route array as `index:route` marks, emitting
 * one only where the route changes. A 120-sample trail of a user sitting on one
 * page costs a single mark.
 */
function encodeRouteTrail(routes: readonly string[]): string {
  const marks: string[] = [];
  let last: string | undefined;
  routes.forEach((route, i) => {
    if (route !== last) {
      marks.push(`${i}:${route}`);
      last = route;
    }
  });
  return marks.join(",");
}

export class MemoryTrail {
  /** phys_footprint in whole MiB, oldest first. */
  private fp: number[] = [];
  /** Route active at each corresponding fp sample. Parallel array, same length. */
  private routes: string[] = [];

  /**
   * Record one sample. A missing reading (plugin absent on web, transient
   * native error) is skipped rather than recorded as a gap — the trail is a
   * shape, and a fabricated zero would read as a collapse.
   *
   * The two arrays are pushed and shifted together, so no index rewriting is
   * ever needed and a route mark cannot drift off its sample.
   */
  record(physFootprintBytes: number | undefined, route: string): void {
    if (typeof physFootprintBytes !== "number" || !Number.isFinite(physFootprintBytes)) return;

    this.fp.push(Math.round(physFootprintBytes / BYTES_PER_MIB));
    this.routes.push(route);

    if (this.fp.length > TRAIL_MAX_SAMPLES) {
      this.fp.shift();
      this.routes.shift();
    }
  }

  /** Context fields to merge into `memory_runtime`. Empty trail contributes nothing. */
  toContext(): Record<string, unknown> {
    if (this.fp.length === 0) return {};
    return {
      fp_trail_mb: this.fp.join(","),
      fp_trail_step_s: TRAIL_STEP_S,
      route_trail: encodeRouteTrail(this.routes),
    };
  }
}
